import type { WorldMemberAttributes } from '@/server/models'

import { joinSQLFragments, SQL, type SQLFragment } from '@/client/query'
import type { LeftWorldReason, WorldHelpIntent } from '@/client/services/worlds'
import { EDITABLE_POST_TYPES } from '@/client/services/worlds/constants'
import { getWorldLevelsAboveAndIncluding } from '@/client/services/worlds/helpers'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOffsetToSQL,
  queryOrderToSQL,
} from './common'
import { WORLD_LEVELS, type WorldLevel } from './constants'
import PostEventQ from './PostEventQ'
import PostQ from './PostQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const WorldMemberQ = {
  ...makeQueryModelV2<WorldMemberAttributes>('world_member')([
    'id',
    'user_id',
    'world_id',
    'level',
    'goal',
    'snooze_goal_until',
    'left_reason',
    'position',
    'details',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: WorldMemberQ.world_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: WorldMemberQ.user_id,
    },
  }),

  hasHelpIntent: (helpIntent: WorldHelpIntent): SQLFragment<boolean> =>
    SQL`${WorldMemberQ.details}->'helpIntents' @> jsonb_build_array(${helpIntent}::text)`,

  where: (
    {
      id = null,
      userId = null,
      worldId = null,
      level = null,
      groupBy = null,
      order = null,
      leftReason = null,
      include = [],
      limit = null,
      offset = null,
      paranoid = true,
      tableSample = null,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      worldId?: number | null | SQLFragment<number>
      level?: WorldLevel | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      leftReason?: LeftWorldReason | null
      include?: IncludeAssociations
      limit?: number | null
      offset?: number | null
      paranoid?: boolean
      tableSample?: SQLFragment<unknown> | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<WorldMemberAttributes> => SQL`
        FROM ${WorldMemberQ}
        ${tableSample != null ? SQL`TABLESAMPLE ${tableSample} ` : SQL``}
        ${includeAssociationsToSql(WorldMemberQ, include)}
        WHERE
            ${id != null ? SQL`${WorldMemberQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${WorldMemberQ.user_id} = ${userId} AND` : SQL``}
            ${worldId != null ? SQL`${WorldMemberQ.world_id} = ${worldId} AND` : SQL``}
            ${level != null ? SQL`${WorldMemberQ.level} = ${level} AND` : SQL``}
            ${
              leftReason != null
                ? SQL`${WorldMemberQ.left_reason} = ${leftReason} AND`
                : SQL`${WorldMemberQ.left_reason} IS NULL AND`
            }
            ${paranoid ? SQL`${WorldMemberQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
        ${queryOffsetToSQL(offset)}
    `,

  countPosts: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostQ.where(
      {
        worldId: WorldMemberQ.world_id,
        userId: WorldMemberQ.user_id,
      },
      SQL`${PostQ.type} = ANY(${EDITABLE_POST_TYPES})`
    )}`,

  isAtLeastLevel: (level: WorldLevel): SQLFragment<boolean> =>
    SQL`(${WorldMemberQ.level} = ANY(${getWorldLevelsAboveAndIncluding(level)}))`,

  countComments: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostEventQ.where(
      {
        userId: WorldMemberQ.user_id,
        type: 'comment',
        include: [
          {
            association: 'Post',
            required: true,
            where: SQL`${PostQ.world_id} = ${WorldMemberQ.world_id}`,
          },
        ],
      },
      SQL`${PostQ.type} = ANY(${EDITABLE_POST_TYPES})`
    )}`,

  getPosition: (): SQLFragment<number> => SQL`(
      CASE ${joinSQLFragments(
        WORLD_LEVELS.map(
          (level, index) => SQL`WHEN ${WorldMemberQ.level} = ${level} THEN ${index}`
        ),
        ' '
      )}
      END
    )`,

  // This function can include other indicators as well going forwards so that we can make a best guess of whether to treat someone as a learner or an intending tutor.
  expressedIntentToTutor: (): SQLFragment<boolean> =>
    SQL`${WorldMemberQ.hasHelpIntent('give-help')}`,
}

export default WorldMemberQ
